import React from 'react';

import Data from 'data';
import Store from 'store';
import Session from 'store/session';

import ReactMarkdown from 'react-markdown/with-html';
import Editor, { defaultToolbar, replaceSelection, getSelection } from './Editor';
  
import { BuildInfoLevel } from 'common';
import { plugins, renderers } from './renderer';

import './BuildInfo.scss';
import EditingContext from 'store/editingContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faEyeSlash, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import Dialog from 'ui/dialog';
import { OverlayContext } from 'ui/utils/OverlayManager';
import Locale from 'data/locale';
import Select, { underlinerFunc } from 'ui/select';
import { itemCategories, renderBase } from 'components/equipment/itemCategories';
import { itemGetName } from 'logic/item';
import { itemTypeTree } from 'logic/itemTypes';
import { D2ItemTooltip, D2SkillTooltip } from 'tooltips';
import { itemNameColored } from 'components/equipment/EquipmentPane';
import shrineBuffs from 'logic/skills/shrines';
import { shrineTipNode } from 'embed/gametips';
import AdUnit from 'AdUnit';

const _L = Locale();

function setText(profile, text) {
  if (profile.buildinfo?.text === text) {
    return profile;
  }
  profile = {...profile};
  profile.buildinfo = {...profile.buildinfo, text};
  return profile;
}

const customLinks = {
  profile: {
    title: () => <span>Profile Item</span>,
    listEx(store) {
      const items = store.getState().items;
      return Object.entries(items).map(([id, item]) => ({
        id,
        name: itemGetName(item),
        item,
        //description: item.description,
      }));
    },
    linkName: opt => opt.name,
    link: opt => `item.profile.${opt.id}`,
    render: ({ id, item, description }, search) => (
      <D2ItemTooltip id={id}>
        <span>
          {itemNameColored(item, search)}
          {!!description && <span className="d2p-item-description">{description}</span>}
        </span>
      </D2ItemTooltip>
    ),
  },
  skill: {
    title: () => <span>Skill</span>,
    list() {
      const list = {};
      for (const [id, skill] of Object.entries(Data.skills)) {
        const desc = Data.skillDesc[skill.skilldesc];
        if (!desc) continue;
        const cls = skill.charclass || '';
        list[cls] = list[cls] || [];
        list[cls].push({id, name: Locale.strings[desc.strname]});
      }
      return ['', ...Data.classes].map(cls => ({
        title: cls ? Locale.strings[Data.classNames[cls]] : 'General',
        options: (list[cls] || []).sort((a, b) => {
          const sa = Data.skills[a.id], sb = Data.skills[b.id];
          if (sa.reqlevel !== sb.reqlevel) return sa.reqlevel - sb.reqlevel;
          return a.name.localeCompare(b.name);
        }),
      }));
    },
    linkName: opt => opt.name,
    link: opt => `skill.${opt.id}`,
    render: ({ id, name }, search) => (
      <D2SkillTooltip id={id} full canLevel="special">
        <span>{search ? underlinerFunc(name, search) : name}</span>
      </D2SkillTooltip>
    ),
  },
  shrine: {
    title: () => <span>Shrine</span>,
    list() {
      return Object.entries(shrineBuffs).map(([id, shrine]) => ({id, name: Locale.strings[shrine.name]}));
    },
    linkName: opt => opt.name,
    link: opt => `shrine.${opt.id}`,
    render: ({ id, name }, search) => shrineTipNode(name, id),
  },
};
customLinks.skillicon = {
  ...customLinks.skill,
  title: () => <span>Skill Icon</span>,
  link: opt => `skillicon.${opt.id}`,
};

const linkTypes = ['profile', 'normal', 'crafted', 'unique', 'set', 'runeword', 'skill', 'shrine', 'skillicon'];
const renderType = id => (itemCategories[id] || customLinks[id]).title();

function LinkDialog({ defaultValue, onResolve, onClose }) {
  const [type, setType] = React.useState(linkTypes[0]);
  const [idOpt, setId] = React.useState();
  const [baseOpt, setBase] = React.useState();

  const store = Store.useStore();

  const onChangeType = React.useCallback(opt => {
    setId(null);
    setBase(null);
    setType(opt);
  }, []);

  const params = itemCategories[type] || customLinks[type];

  const onAccept = React.useCallback(() => {
    let name;
    if (params.linkName) {
      name = params.linkName(idOpt);
      if (!name) return;
    } else {
      const item = params.create(idOpt?.id, baseOpt?.id);
      if (!item) return;
      delete item.name;
      name = itemGetName(item);
    }
    onResolve({id: params.link(idOpt, baseOpt), name});
  }, [onResolve, params, idOpt, baseOpt]);

  const listOptions = React.useCallback(() => params.list ? params.list(itemTypeTree) : params.listEx(store), [params, store]);
  const listBases = React.useCallback(() => idOpt ? params.bases?.(idOpt.id, itemTypeTree) : [], [idOpt, params]);

  return (
    <Dialog className="d2p-LinkDialog" modal title="Insert Link" onClose={onClose}>
      <Dialog.Content>
        <Select options={linkTypes} value={type} onChange={onChangeType} renderOption={renderType} renderValue={renderType}/>
        <Select options={listOptions}
                value={idOpt}
                onChange={setId}
                renderOption={params.render}
                renderValue={params.render}
                searchLimit={10}
                searchMiddle={true}
                virtualScroll/>
        {!!params.linkBases && (
          <Select options={listBases}
                  disabled={!idOpt}
                  value={baseOpt}
                  onChange={setBase}
                  renderOption={renderBase}
                  renderValue={renderBase}
                  searchLimit={10}
                  searchMiddle={true}
                  virtualScroll/>
        )}
      </Dialog.Content>
      <Dialog.Footer>
        <button onClick={onAccept} disabled={!idOpt || (params.linkBases && !baseOpt)}>{_L`Insert`}</button>
        <button onClick={onClose}>{_L`Cancel`}</button>
      </Dialog.Footer>
    </Dialog>
  );
}

const mdOptions = {renderers, plugins};

export default React.memo(function BuildInfo() {
  const [editing, setEditing] = EditingContext.useState("buildInfoEditing", true);
  const canEdit = Session.useSelector(store => (store.userLevel || 0) >= BuildInfoLevel);
  const text = Store.useProfile(profile => profile.buildinfo?.text || "");

  const dispatch = Store.useDispatch();
  const onChange = React.useCallback(value => dispatch.setProfile(profile => setText(profile, value)), [dispatch]);

  const overlays = React.useContext(OverlayContext);

  React.useEffect(() => {
    if (canEdit && !text && !editing) {
      setEditing(true);
    }
  }, [canEdit, editing, text, setEditing]);

  const onEdit = React.useCallback(() => setEditing(true), [setEditing]);

  const commands = React.useMemo(() => {
    const commands = [...defaultToolbar];
    commands.push({
      title: 'Item Link',
      icon: <FontAwesomeIcon icon={faShieldAlt}/>,
      hotkey: 'Ctrl+D',
      async action(editor) {
        const sel = getSelection(editor.current);
        const item = await overlays.async(<LinkDialog defaultValue={sel.text}/>);
        if (item) {
          replaceSelection(editor.current, sel, `${item.prefix || ""}[${item.name}](${item.id})${item.suffix || ""}`,
            1 + (item.prefix?.length || 0), 1 + item.name.length + (item.prefix?.length || 0));
        }
      },
    });
    if (text.length) {
      commands.push({
        name: 'Hide',
        icon: <FontAwesomeIcon icon={faEyeSlash}/>,
        className: "d2p-float-right",
        action() {
          setEditing(false);
        },
      });
    }
    return commands;
  }, [overlays, text, setEditing]);

  return <>
    <div className="d2p-TabContent d2p-BuildInfo">
      {canEdit && editing && (
        <Editor value={text} onChange={onChange} toolbar={commands}/>
      )}
      {canEdit && !editing && (
        <div className="d2md-edit" onClick={onEdit}>
          <FontAwesomeIcon icon={faEdit}/>
        </div>
      )}
      <div className="d2md-preview">
        <ReactMarkdown {...mdOptions} children={text} allowDangerousHtml/>
      </div>
    </div>
    <AdUnit id="d2planner-tab-guide" className="d2p-TabFooter d2planner-tab-ad"/>
  </>;
});
